import { RowData } from "@tanstack/react-table";
import clsx from "clsx";
import { ReactNode, useContext } from "react";
import { useRecoilValue } from "recoil";

import { usePageContext } from "@ryde/redux";
import {
  IExtendableDto,
  INotificationDto,
  IRydeRow,
  isHighlightedAfterEditSF,
  isRowExpandedSF,
  isSingleSelectedRideIdSF,
  RenderSubComponentModel,
  RydeUiUnit,
} from "@ryde/shared";

import { useTableRowIsSelected } from "../generic/table/selection-context";
import { TableWrapperContext } from "../generic/table/table-wrapper.context";
import { GridTableCell } from "./grid.table.cell";
import { GridTableDetailsPanel } from "./grid.table.details-panel";

const rowTransparentResolvers: Partial<
  Record<RydeUiUnit, (row: IRydeRow<IExtendableDto>) => boolean>
> = {
  notification: (row: IRydeRow<IExtendableDto>) => {
    return (row.original as INotificationDto).isRead;
  },
};

interface IGetIsTransparentResolveArguments {
  row: IRydeRow<IExtendableDto>;
  entity: RydeUiUnit;
}

const getIsTransparentResolve = ({
  row,
  entity,
}: IGetIsTransparentResolveArguments): boolean => {
  if (rowTransparentResolvers[entity] !== undefined) {
    return rowTransparentResolvers[entity](row);
  }

  return false;
};

export interface IGridRowProps<T extends RowData> {
  row: IRydeRow<T>;
  renderSubComponent?: RenderSubComponentModel<T>;
  /**
   * Ordered visible column ids of the rendered header row. Row renderers memoized on `row` identity MUST
   * include it in their comparator: react-table keeps the same row object when the column order or
   * visibility changes, so without it the body keeps the previous column order under the new headers.
   */
  columnsLayoutSignature: string;
}

export const GridRow = <T extends IExtendableDto>({
  row,
  renderSubComponent,
}: IGridRowProps<T>): ReactNode => {
  const { entity } = usePageContext();
  const { isGridSingleSelectView } = useContext(TableWrapperContext);
  const id = row.original.id;
  const highlighted = useRecoilValue(isHighlightedAfterEditSF([entity, id]));
  const isRowBulkSelected = useTableRowIsSelected(entity, id);
  const isSingleSelectedRow = useRecoilValue(isSingleSelectedRideIdSF(id));
  const rideSingleSelectCheckboxHighlight =
    entity === "ride" && isGridSingleSelectView && isSingleSelectedRow;
  const highlightedSingleRow =
    rideSingleSelectCheckboxHighlight ||
    (!(entity === "ride" && isGridSingleSelectView) && isRowBulkSelected);

  const isRowExpanded = useRecoilValue(isRowExpandedSF([entity, id]));

  return (
    <>
      <tr
        data-e2e="table-row"
        data-e2e-row-id={id}
        className={clsx({
          expanded: isRowExpanded,
          highlighted: highlighted || highlightedSingleRow,
          transparent: getIsTransparentResolve({
            row: row as unknown as IRydeRow<IExtendableDto>,
            entity,
          }),
        })}
      >
        {row.getVisibleCells().map(cell => (
          <GridTableCell<T> key={cell.id} cell={cell} />
        ))}
      </tr>
      {renderSubComponent && (
        <GridTableDetailsPanel
          row={row}
          renderSubComponent={renderSubComponent}
        />
      )}
    </>
  );
};
